export type WordSeverity = 'red' | 'orange' | 'yellow';

export type Location = 'subject' | 'preheader' | 'body';

export interface WordEntry {
  phrase: string;
  severity: WordSeverity;
  location?: 'subject' | 'body' | 'any';
  reason: string;
  suggestion: string;
  contextRule?: 'finance_downgrade' | 'requires_urgency_modifier';
}

export const RED_WORDS: WordEntry[] = [
  {
    phrase: 'Jackpot',
    severity: 'red',
    reason: 'Klassisches Scam-/Glücksspiel-Signal',
    suggestion: 'Begriff vollständig vermeiden',
  },
  {
    phrase: 'Reichtum',
    severity: 'red',
    reason: 'Unrealistisches Wohlstandsversprechen',
    suggestion: "Konkreten Nutzen nennen (z.B. 'Rendite', 'Vermögensaufbau')",
  },
  {
    phrase: 'sofort Geld',
    severity: 'red',
    reason: 'Urgency + Geld-Versprechen — Scam-Pattern',
    suggestion: 'Timing weglassen, Nutzen konkret beschreiben',
  },
  {
    phrase: 'finanziell frei',
    severity: 'red',
    reason: 'Finanz-Coaching-Scam-Vokabular',
    suggestion: "Konkretes Ziel statt Phrase ('langfristiger Vermögensaufbau')",
  },
  {
    phrase: '100% kostenlos',
    severity: 'red',
    reason: 'Klassischer Spam-Marker (Prozent + kostenlos)',
    suggestion: "Nur 'kostenlos' allein reicht völlig",
  },
  {
    phrase: 'Gewinngarantie',
    severity: 'red',
    reason: 'Unmögliches Versprechen — Finanzaufsicht-relevant',
    suggestion: 'Begriff entfernen (rechtlich ohnehin heikel)',
  },
  {
    phrase: 'unschlagbar',
    severity: 'red',
    reason: 'Übertriebenes Marketing-Signal',
    suggestion: 'Konkrete Zahl oder Vergleich statt Superlativ',
  },
  {
    phrase: 'Geld verdienen',
    severity: 'red',
    reason: 'Klassisches MLM-/Scam-Pattern',
    suggestion: "'Rendite', 'Ertrag', 'Einnahmen' je nach Kontext",
  },
  {
    phrase: 'garantiert reich',
    severity: 'red',
    reason: 'Extreme Versprechen — rotes Tuch für Filter',
    suggestion: 'Begriff ersatzlos streichen',
  },
  {
    phrase: 'Sie haben gewonnen',
    severity: 'red',
    reason: 'Klassisches Phishing-Betreff-Muster',
    suggestion: 'Niemals verwenden',
  },
  {
    phrase: 'Goldgrube',
    severity: 'red',
    reason: 'Scam-adjacent Reichtums-Metapher',
    suggestion: "'Wachstums-Chance' oder konkreter Markt",
  },
  {
    phrase: 'steinreich',
    severity: 'red',
    reason: 'Reichtums-Versprechen',
    suggestion: "'Vermögensaufbau' ist neutraler",
  },
  {
    phrase: 'Vermögensumverteilung',
    severity: 'red',
    reason: 'Scam-/Verschwörungs-Framing',
    suggestion: 'Sachlichen Trend beschreiben',
  },
  {
    phrase: 'Goldrausch',
    severity: 'red',
    reason: 'FOMO-/Reichtums-Metapher',
    suggestion: 'Nüchterne Markt-Beschreibung',
  },
];

export const ORANGE_WORDS: WordEntry[] = [
  {
    phrase: 'Gewinn',
    severity: 'orange',
    location: 'subject',
    reason: 'Im Betreff Spam-Signal (im Body mit Finanz-Kontext ok)',
    suggestion: "Betreff konkreter: 'Rendite-Update', 'Ertrag', 'Performance'",
    contextRule: 'finance_downgrade',
  },
  {
    phrase: 'Bonus',
    severity: 'orange',
    reason: "Marketing-Cliché, in Verbindung mit 'sichern'/'jetzt' kritisch",
    suggestion: "'Zusatzleistung', 'Extra' oder weglassen",
  },
  {
    phrase: 'Prämie',
    severity: 'orange',
    reason: 'Ähnlich wie Bonus — als Dringlichkeits-Hook riskant',
    suggestion: 'Konkreten Vorteil beschreiben',
  },
  {
    phrase: 'Cash',
    severity: 'orange',
    reason: 'Anglizismus — wirkt Money-Mail-ähnlich',
    suggestion: "'Bargeld', 'Geldbetrag' oder Anglizismus vermeiden",
  },
  {
    phrase: 'Geschenk sichern',
    severity: 'orange',
    reason: 'Klassisches Urgency-Muster',
    suggestion: "Neutrales Wording: 'jetzt herunterladen', 'hier anfordern'",
  },
  {
    phrase: 'jetzt kaufen',
    severity: 'orange',
    reason: 'Direkte Kauf-CTA mit Dringlichkeit',
    suggestion: "'mehr erfahren', 'zum Angebot' (weniger aggressiv)",
  },
  {
    phrase: 'stark reduziert',
    severity: 'orange',
    reason: 'Rabatt-Urgency-Kombination',
    suggestion: 'Konkrete Prozent-Angabe statt Superlativ',
  },
  {
    phrase: 'billig',
    severity: 'orange',
    reason: 'Negatives Qualitätssignal bei Filtern',
    suggestion: "'günstig', 'preiswert' verwenden",
  },
  {
    phrase: 'Nicht verpassen',
    severity: 'orange',
    reason: 'Urgency-Cliché',
    suggestion: 'Inhaltlichen Hook statt Zeitdruck',
  },
  {
    phrase: 'Letzte Chance',
    severity: 'orange',
    reason: 'Klassisches FOMO-Signal',
    suggestion: 'Deadline konkret nennen statt Phrase',
  },
  {
    phrase: 'Nur heute',
    severity: 'orange',
    reason: 'Urgency-Push ohne Mehrwert',
    suggestion: 'Genaues Datum + konkreter Nutzen',
  },
  {
    phrase: 'Einkommen steigern',
    severity: 'orange',
    reason: 'MLM-/Coaching-Vokabular',
    suggestion: "'Rendite erhöhen', 'zusätzlicher Ertrag'",
  },
  {
    phrase: 'Sofort verfügbar',
    severity: 'orange',
    reason: 'Urgency-Signal',
    suggestion: "'ab sofort' oder Datum nennen",
  },
  {
    phrase: 'Handeln Sie jetzt',
    severity: 'orange',
    reason: 'Aggressive CTA',
    suggestion: 'Sachliche Aufforderung',
  },
  {
    phrase: 'Provision',
    severity: 'orange',
    reason: 'Nur bei Urgency-Kombi problematisch (allein akzeptabel)',
    suggestion: 'Ohne Urgency-Modifier unbedenklich',
    contextRule: 'requires_urgency_modifier',
  },
  {
    phrase: 'Masterplan',
    severity: 'orange',
    reason: 'Marketing-Übertreibung in Finanz-Kontext',
    suggestion: "'Strategie', 'Konzept', 'Ansatz'",
  },
  {
    phrase: 'Enthüllung',
    severity: 'orange',
    reason: 'Sensations-Rhetorik, typisch für Scam-Promos',
    suggestion: "'Analyse', 'Überblick', 'Einblick'",
  },
  {
    phrase: 'Enthüllungsvideo',
    severity: 'orange',
    reason: 'Clickbait-Format-Bezeichnung',
    suggestion: "'Webinar', 'Präsentation', 'Video-Analyse'",
  },
  {
    phrase: 'Megatrend',
    severity: 'orange',
    reason: 'Übertreibungs-Marketing',
    suggestion: "'Wachstums-Trend', 'Branchen-Entwicklung'",
  },
  {
    phrase: 'Schlachtplan',
    severity: 'orange',
    reason: 'Kriegs-Metaphorik, Urgency-Signal',
    suggestion: "'Strategie', 'Vorgehen'",
  },
  {
    phrase: 'geheim',
    severity: 'orange',
    reason: 'Scam-adjacent Exklusivitäts-Signal',
    suggestion: "'exklusiv' (wenn belegbar) oder weglassen",
  },
  {
    phrase: 'Cash-Rallye',
    severity: 'orange',
    reason: 'Reichtums-Framing + Urgency-Kombi',
    suggestion: 'Sachlichen Markt-Bezug wählen',
  },
  {
    phrase: 'Wette',
    severity: 'orange',
    reason: 'Glücksspiel-Framing in Finanzkontext',
    suggestion: "'Anlage-Entscheidung', 'Position'",
  },
  {
    phrase: 'Nestei',
    severity: 'orange',
    reason: 'Euphemismus für Geldversprechen (Kontext-spezifisch)',
    suggestion: 'Konkreten Finanzbegriff nutzen',
  },
];

export const YELLOW_WORDS: WordEntry[] = [
  {
    phrase: 'gratis',
    severity: 'yellow',
    reason: "Alternative zu 'kostenlos' — häufiger in Spam",
    suggestion: "'kostenlos' ist neutraler",
  },
  {
    phrase: 'kostenlos',
    severity: 'yellow',
    reason: 'Einzeln ok, in Kombination (100%, Geschenk) kritisch',
    suggestion: 'Nur verwenden wenn sachlich zutreffend',
  },
  {
    phrase: 'Rabatt',
    severity: 'yellow',
    reason: 'Commerce-Wort, in Wiederholung Spam-verdächtig',
    suggestion: 'Konkreten Prozentwert verwenden',
  },
  {
    phrase: 'Angebot',
    severity: 'yellow',
    reason: 'Neutraler Geschäftsbegriff — in Häufung auffällig',
    suggestion: 'Ok sparsam, nicht mehrfach pro Mail',
  },
  {
    phrase: 'Deal',
    severity: 'yellow',
    reason: 'Anglizismus — gaukelt oft Schnäppchen vor',
    suggestion: "'Angebot' ist neutraler",
  },
  {
    phrase: 'Sonderangebot',
    severity: 'yellow',
    reason: 'Marketing-Standard, gehäuft kritisch',
    suggestion: 'Konkrete Aktion benennen',
  },
  {
    phrase: 'Geschenk',
    severity: 'yellow',
    reason: 'Ok wenn tatsächlich kostenlos — nicht als Lockmittel',
    suggestion: "Präzise benennen ('kostenloses PDF', 'Gratis-Webinar')",
  },
  {
    phrase: 'Schnäppchen',
    severity: 'yellow',
    reason: 'Preis-Urgency-Signal',
    suggestion: "'günstig' oder konkreter Preis",
  },
  {
    phrase: 'Top-Empfehlung',
    severity: 'yellow',
    reason: 'Superlativ ohne Beleg',
    suggestion: "Mit Daten untermauern ('Analyse zeigt...')",
  },
];

export const ABBREVIATION_WHITELIST = [
  'ETF',
  'ETFS',
  'KI',
  'AI',
  'USA',
  'USD',
  'EUR',
  'DAX',
  'MDAX',
  'TECDAX',
  'CEO',
  'CFO',
  'COO',
  'ROI',
  'KPI',
  'LBA',
  'GP',
  'IT',
  'EU',
  'UK',
  'IPO',
  'ESG',
  'MSCI',
  'NYSE',
  'NASDAQ',
  'BTC',
  'ETH',
  'GBP',
  'CHF',
  'JPY',
  'CNY',
  'OECD',
  'IWF',
  'EZB',
  'FED',
  'BRICS',
  'NATO',
  'G7',
  'G20',
  'SAM',
  'NO',
  'PLX',
  'VNR',
  'CPA',
  'LTV',
  'HTML',
  'PDF',
  'URL',
  'FAQ',
  'DIY',
  'DSGVO',
  'BaFin',
  'WKN',
  'ISIN',
  'IBAN',
  'BIC',
];

export const FINANCE_CONTEXT_WORDS = [
  'aktien',
  'aktie',
  'börse',
  'rendite',
  'etf',
  'fond',
  'fonds',
  'dividende',
  'kurs',
  'depot',
  'portfolio',
  'anleihe',
  'wertpapier',
  'hebelprodukt',
  'derivat',
  'optionsschein',
  'trading',
  'investment',
  'anleger',
  'vermögen',
  'bilanz',
  'quartal',
  'analyse',
  'dax',
  'nasdaq',
  'wall street',
  'bloomberg',
  'unternehmen',
  'emittent',
  'ausschüttung',
  'kapitalmarkt',
  'kgv',
  'ebit',
  'ebitda',
  'umsatz',
  'marktkapitalisierung',
];

export const URGENCY_MODIFIERS = [
  'jetzt',
  'sofort',
  'schnell',
  'eilig',
  'heute',
  'nur',
  'dringend',
  'hurtig',
  'blitzschnell',
  'achtung',
];

export const ALL_WORDS: WordEntry[] = [...RED_WORDS, ...ORANGE_WORDS, ...YELLOW_WORDS];
